import React, { useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { sizes, getWeekdays } from 'helpers/utils'
import DashboardChart, { renderBarTooltipTemplate } from 'components/DashboardChart/DashboardChart'
import { renderSalesTooltip } from 'components/DashboardChart/tooltipTemplates'
import { StyledDashboardChart } from 'style/mainChartsStyles'

import CompareButton from './CompareButton'

const timeRange = Array.from(Array(23).keys()).slice(6).reduce((acc, hour) => {
	const range = [14, 29, 44, 59]
	return acc.concat(range.map(m => moment(`${hour}:${!m ? '00' : m}`, 'HH:mm')))
}, [])

export const LineChart = ({
	bcResponse,
	bsResponse,
	dateInterval,
	formatedDate,
	activeDate,
	setCompareDate,
	compareDate
}) => {
	const [dropdownVisible, setDropdownVisible] = useState(false)
	const [t] = useTranslation()

	const { choosenDay } = compareDate

	const filteredTodayBills = bcResponse.data?.getCustomerFrequencies?.filter(b =>
		moment.utc(formatedDate).valueOf() === moment.utc(b.dateTime).valueOf())

	const filteredComparedBills = (bsResponse.data?.getCustomerFrequencies
		|| bcResponse.data?.getCustomerFrequencies)?.filter(b =>
		moment.utc(choosenDay).valueOf() === moment.utc(b.dateTime).valueOf())

	let chartSeries = [
		{ value: formatedDate, name: formatedDate, color: '#4A90E2' }
	]
	if (choosenDay !== formatedDate) {
		chartSeries.push({ value: choosenDay, name: choosenDay, color: '#F2874F' })
	}

	const data = timeRange.map(dateTime => {
		const currentTime = moment(dateTime).format('HH:mm')
		let dataSource = {
			dateTime: moment(dateTime).add(1, 'minutes').valueOf(),
			[formatedDate]: filteredTodayBills?.find(b => {
				return b.interval.split('-')[1] === currentTime
			})?.billCount || 0
		}
		if (choosenDay !== formatedDate) {
			dataSource[choosenDay] = filteredComparedBills?.find(b => {
				return b.interval.split('-')[1] === currentTime
			})?.billCount || 0
		}
		return dataSource[choosenDay] || dataSource[formatedDate] ? dataSource : null
	}).filter(item => item)

	return(
		<StyledDashboardChart className='lineChart'>
			<DashboardChart
				headerItems={[
					(<h3 className='dchart__title'>{t('dashboard_section.customers')}</h3>),
					(<div>
						<CompareButton
							activeDate={activeDate}
							compareDate={compareDate}
							setCompareDate={setCompareDate}
							dateInterval={dateInterval}
							dropdownVisible={dropdownVisible}
							setDropdownVisible={setDropdownVisible}
							fetchSpecialData={() => {}}
						/>
					</div>)
				]}
				fetchError={bcResponse.error || bsResponse.error}
				dataLoading={bcResponse.loading || bsResponse.loading}
				data={data}
				series={chartSeries}
				type={'line'}
				choosenLabel={compareDate.choosenLabel}
			/>
		</StyledDashboardChart>
	)
}

export const SalesLineChart = ({
	bcResponse,
	bsResponse,
	compareDate,
	formatedDate,
	mainCurrency
}) => {
	const [t] = useTranslation()
	const isMobile = useMediaQuery({ query: `(max-width: ${sizes.mobile}px)` })
	const { choosenDay } = compareDate

	const filteredTodayBills = bcResponse.data?.getCustomerFrequencies?.filter(b =>
		moment.utc(formatedDate).valueOf() === moment.utc(b.dateTime).valueOf())

	const filteredComparedBills = (bsResponse.data?.getCustomerFrequencies
		|| bcResponse.data?.getCustomerFrequencies)?.filter(b =>
		moment.utc(choosenDay).valueOf() === moment.utc(b.dateTime).valueOf())

	let chartSeries = [
		{ value: formatedDate, name: formatedDate, color: '#45DB5D' }
	]
	if (choosenDay !== formatedDate) {
		chartSeries.push({ value: choosenDay, name: choosenDay, color: '#F2874F' })
	}

	return(
		<StyledDashboardChart className='salesLine'>
			<DashboardChart
				headerItems={[
					(<h3 className='dchart__title'>{t('dashboard_section.total')}</h3>)
				]}
				fetchError={bcResponse.error || bsResponse.error}
				dataLoading={bcResponse.loading || bsResponse.loading}
				containerProps={{
					height: isMobile ? 200 : 400
				}}
				areaChartProps={{
					data: timeRange.map(dateTime => {
						const currentTime = moment(dateTime).format('HH:mm')
						let dataSource = {
							dateTime: moment(dateTime).add(1, 'minutes').valueOf(),
							[formatedDate]: filteredTodayBills?.find(b => {
								return b.interval.split('-')[1] === currentTime
							})?.total || 0
						}
						if (choosenDay !== formatedDate) {
							dataSource[choosenDay] = filteredComparedBills?.find(b => {
								return b.interval.split('-')[1] === currentTime
							})?.total || 0
						}
						return dataSource[choosenDay] || dataSource[formatedDate] ? dataSource : null
					}).filter(item => item),
					margin: { left: -10 }
				}}
				xAxisProps={{
					scale: 'time',
					type: 'number',
					dataKey: 'dateTime',
					interval: 4,
					domain: ['dataMin', 'dataMax'],
					tickFormatter: (tick) => moment(tick).format('HH:mm')
				}}
				yAxisProps={{
					tickFormatter: (tick) => String(tick).length > 3
						? `${(tick / 1000).toFixed(1)}K`
						: tick
				}}
				tooltipProps={{
					content: contentProps => {
						const { payload, active } = contentProps
						if (active && payload) {
							const { dateTime } = payload[0].payload
							const currentDate = moment(dateTime)
							const headerValue = `${currentDate.clone().subtract(15, 'minutes').format('HH:mm')} - ${currentDate.format('HH:mm')}`
							return (payload[0].value || payload[1]?.value) ? renderSalesTooltip(t, {
								headerText: 'loyalty_card_section.strings.period',
								footerText: 'dashboard_section.total',
								compareText: 'dashboard_section.total',
								headerValue,
								footerValue: `${payload[0].value.toFixed(2)} ${mainCurrency}`,
								compareValue: `${payload[1]?.value.toFixed(2)} ${mainCurrency}`
							})(contentProps) : null
						}
						return null
					}
				}}
				type='ldchart'
				series={chartSeries}
			/>
		</StyledDashboardChart>
	)
}


export const BarChart = ({
	bcResponse,
	formatedDateTo,
	formatedDateFrom
}) => {
	const [t] = useTranslation()
	const isMobile = useMediaQuery({ query: `(max-width: ${sizes.mobile}px)` })
	const weekdays = getWeekdays()
	let totalNumber = 0, barDate = null
	const dataSource = weekdays.reduce((acc, day) => {
		const customerCount = bcResponse.data?.getCustomerFrequencies?.reduce((ac, b) => {
			if (moment(b.dateTime).format('dddd') === day) {
				barDate = b.dateTime
				return ac + (b.billCount || 0)
			}
			return ac
		}, 0) || 0
		totalNumber += customerCount
		return [...acc, {
			day,
			customers: customerCount,
			date: barDate
		}]
	}, [])

	return (
		<StyledDashboardChart className='barChart'>
			<DashboardChart
				headerItems={[
					(<div className='dchart__wrapper'>
						<h3 className='dchart__title'>{t('dashboard_section.branch_details_bar_title')}</h3>
						<span className='dchart__date_interval dchart__date_interval_customer'>
							{formatedDateFrom} - {formatedDateTo}
						</span>
					</div>
					),
					(
						<div className='dchart__total'>
							<h3 className='dchart__total__label'>
								{t('dashboard_section.total_branch')}:
							</h3>
							<span className='dchart__total__value'>
								{totalNumber}
							</span>
						</div>
					)
				]}
				dataSource={dataSource}
				fetchError={bcResponse.error}
				dataLoading={bcResponse.loading}
				containerProps={{
					height: isMobile ? 200 : 400
				}}
				barChartProps={{
					data: dataSource,
					margin: { left: -10 }
				}}
				xAxisProps={{
					dataKey: 'day'
				}}
				yAxisProps={{
					tickFormatter: (tick) => String(tick).length > 3
						? `${(tick / 1000).toFixed(1)}K`
						: tick
				}}
				barProps={{
					dataKey: 'customers'
				}}
				tooltipProps={{
					content: renderBarTooltipTemplate(t)
				}}
				type='bdchart'
				color='#589ffb'
			/>
		</StyledDashboardChart>
	)
}

export const SalesBarChart = ({
	bcResponse,
	formatedDateFrom,
	formatedDateTo,
	mainCurrency
}) => {
	const [t] = useTranslation()
	const isMobile = useMediaQuery({ query: `(max-width: ${sizes.mobile}px)` })
	const weekdays = getWeekdays()
	let totalCount = 0, barDate = null
	const dataSource = weekdays.reduce((acc, day) => {
		const dayTotal = bcResponse.data?.getCustomerFrequencies?.reduce((ac, b) => {
			if (moment(b.dateTime).format('dddd') === day) {
				barDate = b.dateTime
				return ac + (b.total || 0)
			}
			return ac
		}, 0)
		totalCount += dayTotal
		return [...acc, {
			day,
			total: dayTotal,
			date: barDate
		}]
	}, [])

	return(
		<StyledDashboardChart className='salesBar'>
			<DashboardChart
				headerItems={[
					(<div className='dchart__wrapper'>
						<h3 className='dchart__title'>{t('dashboard_section.branch_details_bar_sales_title')}</h3>
						<span className='dchart__date_interval dchart__date_interval_sales'>
							{formatedDateFrom} - {formatedDateTo}
						</span>
					</div>
					),
					(
						<div className='dchart__total dchart__total_sales'>
							<h3 className='dchart__total__label'>
								{t('dashboard_section.total_branch')}:
							</h3>
							<span className='dchart__total__value'>
								{totalCount.toFixed(2)} {mainCurrency}
							</span>
						</div>
					)
				]}
				dataSource={dataSource}
				fetchError={bcResponse.error}
				dataLoading={bcResponse.loading}
				containerProps={{
					height: isMobile ? 200 : 400
				}}
				barChartProps={{
					data: dataSource,
					margin: { left: -10 }
				}}
				xAxisProps={{
					dataKey: 'day'
				}}
				yAxisProps={{
					tickFormatter: (tick) => String(tick).length > 3
						? `${(tick / 1000).toFixed(1)}K`
						: tick
				}}
				barProps={{
					dataKey: 'total'
				}}
				tooltipProps={{
					content: renderBarTooltipTemplate(t, { currency: mainCurrency, label: 'dashboard_section.total' })
				}}
				type='bdchart'
				color='#53cab4'
			/>
		</StyledDashboardChart>
	)
}
